// Book reading tracker — types, metadata, storage and Goodreads CSV import.

export type BookStatus = "read" | "reading" | "want";
export type BookSource = "storytel" | "goodreads" | "other";
export type BookFormat = "audio" | "ebook" | "print" | "";

export interface Book {
  id: string;
  title: string;
  author: string;
  status: BookStatus;
  rating: number; // 0–5 (0 = unrated)
  source: BookSource;
  format: BookFormat;
  year: string; // free-text year read, e.g. "2024"
  notes: string;
  coverUrl?: string; // cover image
  isbn?: string; // ISBN of the picked edition (helps re-fetch covers)
  genre?: string; // genre / shelf label
}

export const STATUS_META: Record<BookStatus, { label: string; icon: string; color: string }> = {
  read: { label: "Läst", icon: "✅", color: "#3aa76d" },
  reading: { label: "Läser", icon: "📖", color: "#e2b31a" },
  want: { label: "Vill läsa", icon: "🔖", color: "#9b6dd6" },
};
export const STATUS_ORDER: BookStatus[] = ["read", "reading", "want"];

export const SOURCE_META: Record<BookSource, { label: string; icon: string }> = {
  storytel: { label: "Storytel", icon: "🎧" },
  goodreads: { label: "Goodreads", icon: "📚" },
  other: { label: "Annat", icon: "📕" },
};
export const SOURCE_ORDER: BookSource[] = ["storytel", "goodreads", "other"];

export const FORMAT_META: Record<Exclude<BookFormat, "">, { label: string; icon: string }> = {
  audio: { label: "Ljudbok", icon: "🎧" },
  ebook: { label: "E-bok", icon: "📱" },
  print: { label: "Fysisk", icon: "📖" },
};
export const FORMAT_ORDER: BookFormat[] = ["audio", "ebook", "print"];

export const newId = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export const blankBook = (): Book => ({
  id: newId(),
  title: "",
  author: "",
  status: "read",
  rating: 0,
  source: "storytel",
  format: "audio",
  year: String(new Date().getFullYear()),
  notes: "",
  genre: "",
});

const STORE_KEY = "books-v1";

export const loadBooks = (): Book[] => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map(normalizeBook) : [];
  } catch {
    return [];
  }
};

export const saveBooks = (books: Book[]): void => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(books));
  } catch {
    /* storage unavailable */
  }
};

export const normalizeBook = (b: any): Book => ({
  id: b?.id ?? newId(),
  title: b?.title ?? "",
  author: b?.author ?? "",
  status: (["read", "reading", "want"].includes(b?.status) ? b.status : "read") as BookStatus,
  rating: typeof b?.rating === "number" ? Math.max(0, Math.min(5, Math.round(b.rating))) : 0,
  source: (["storytel", "goodreads", "other"].includes(b?.source) ? b.source : "other") as BookSource,
  format: (["audio", "ebook", "print", ""].includes(b?.format) ? b.format : "") as BookFormat,
  year: b?.year ? String(b.year) : "",
  notes: b?.notes ?? "",
  coverUrl: b?.coverUrl ?? "",
  isbn: b?.isbn ?? "",
  genre: b?.genre ?? "",
});

// Open Library cover from an ISBN (404s when missing → the <img> falls back).
export const coverFromIsbn = (isbn: string): string =>
  `https://covers.openlibrary.org/b/isbn/${isbn}-M.jpg?default=false`;

export interface BookSuggestion { title: string; author: string; coverUrl: string; year: string; isbn: string; genre: string }

// Map an arbitrary category/subject string to one of a curated set of common
// genres (Swedish). Order matters — more specific rules first. Returns "" for
// anything that isn't a real genre (e.g. Open Library's "Orphans"), so those
// books land under "Övrigt" instead of a junk shelf.
const GENRE_RULES: [RegExp, string][] = [
  [/true crime/, "True crime"],
  [/thriller|mystery|detective|\bcrime\b|deckare|spänning|noir/, "Deckare & thriller"],
  [/sci-?fi|science[\s-]?fiction/, "Science fiction"],
  [/fantasy/, "Fantasy"],
  [/horror|skräck/, "Skräck"],
  [/romance|romantik|kärleksroman/, "Romance"],
  [/biograph|autobiograph|memoir|biografi|självbiograf/, "Biografi & memoarer"],
  [/self-?help|självhjälp|personal (development|growth)|mindfulness/, "Självhjälp"],
  [/psycholog|psykolog/, "Psykologi"],
  [/philosoph|filosofi/, "Filosofi"],
  [/business|econom|ekonomi|management|finance|leadership|entrepren/, "Ekonomi & business"],
  [/health|fitness|hälsa|wellness|\bkost\b|träning/, "Hälsa & träning"],
  [/cook|\bfood\b|recipe|matlag|mat & dryck/, "Mat & dryck"],
  [/travel|resor|reseskildr/, "Resor"],
  [/religio|christian|spiritual|andlig|buddh|teolog/, "Religion & andlighet"],
  [/poetry|poesi|lyrik/, "Poesi"],
  [/comics|graphic novel|serier|manga/, "Serier"],
  [/history|histor/, "Historia"],
  [/science|vetenskap|physics|biology|nature|teknik/, "Vetenskap"],
  [/fiction|roman|novel|skönlitt|litteratur/, "Skönlitteratur"],
];
// The curated set of genres we allow (canonical Swedish labels).
export const KNOWN_GENRES = Array.from(new Set(GENRE_RULES.map((r) => r[1])));
export const isKnownGenre = (g: string): boolean => KNOWN_GENRES.includes((g || "").trim());

// Classify by the DOMINANT genre across a list of subjects/categories: each
// subject votes for its first matching rule, and the genre with the most votes
// wins (ties broken by rule order). This is robust to noisy Open Library subject
// lists where one stray "mystery" tag shouldn't override a mostly-Fantasy book.
const classifyBest = (list: any): string => {
  const arr = Array.isArray(list) ? list : list ? [list] : [];
  const votes = new Map<string, number>();
  for (const c of arr) {
    const s = String(c).toLowerCase();
    for (const [re, name] of GENRE_RULES) {
      if (re.test(s)) { votes.set(name, (votes.get(name) || 0) + 1); break; }
    }
  }
  if (votes.size === 0) return "";
  const idxOf = (name: string) => GENRE_RULES.findIndex((r) => r[1] === name);
  // "Skönlitteratur" (generic fiction) is a fallback — nearly every novel carries
  // many "fiction" tags, so a specific genre (Fantasy, Sci-fi…) should win even
  // with fewer votes. Only fall back to generic fiction if nothing specific hit.
  const GENERIC = "Skönlitteratur";
  let bestName = "";
  let bestVotes = -1;
  let bestIdx = GENRE_RULES.length;
  votes.forEach((count, name) => {
    if (name === GENERIC) return;
    const idx = idxOf(name);
    if (count > bestVotes || (count === bestVotes && idx < bestIdx)) {
      bestVotes = count;
      bestName = name;
      bestIdx = idx;
    }
  });
  if (bestName) return bestName;
  return votes.has(GENERIC) ? GENERIC : "";
};

// "Lastname, Firstname, 1967-" → "Firstname Lastname" (drops year segments).
const flipName = (s: string): string => {
  if (!s) return "";
  const parts = s.split(",").map((x) => x.trim()).filter((x) => x && !/^\d/.test(x));
  if (parts.length >= 2) return `${parts[1]} ${parts[0]}`;
  return parts[0] || s;
};

// Libris (Swedish national library, kb.se) — best coverage of Swedish books.
const searchLibris = async (q: string, signal?: AbortSignal): Promise<BookSuggestion[]> => {
  const res = await fetch(`https://libris.kb.se/xsearch?query=${encodeURIComponent(q)}&format=json&n=8`, { signal });
  if (!res.ok) return [];
  const data = await res.json();
  const list: any[] = data?.xsearch?.list || [];
  const seen = new Set<string>();
  const out: BookSuggestion[] = [];
  for (const r of list) {
    const title = (r.title || "").trim();
    if (!title || seen.has(title.toLowerCase())) continue;
    seen.add(title.toLowerCase());
    const isbnRaw = Array.isArray(r.isbn) ? r.isbn[0] : r.isbn;
    const isbn = String(isbnRaw || "").replace(/[^0-9Xx]/g, "");
    const dateRaw = Array.isArray(r.date) ? r.date[0] : r.date;
    const ym = /(\d{4})/.exec(String(dateRaw || ""));
    out.push({
      title,
      author: flipName(r.creator || ""),
      coverUrl: isbn.length >= 10 ? coverFromIsbn(isbn) : "",
      year: ym ? ym[1] : "",
      isbn,
      genre: "",
    });
  }
  return out;
};

const gIsbn = (v: any): string => {
  const ids: any[] = v?.industryIdentifiers || [];
  const i13 = ids.find((x) => x.type === "ISBN_13");
  const i10 = ids.find((x) => x.type === "ISBN_10");
  return String(i13?.identifier || i10?.identifier || "").replace(/[^0-9Xx]/g, "");
};

const googleThumb = (v: any): string => {
  const l = v?.imageLinks || {};
  const url = l.thumbnail || l.smallThumbnail || "";
  return url ? url.replace(/^http:/, "https:") : "";
};

// Google Books search — good Swedish coverage, keyless.
const searchGoogle = async (q: string, signal?: AbortSignal): Promise<BookSuggestion[]> => {
  const res = await fetch(
    `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(q)}&maxResults=7&printType=books`,
    { signal }
  );
  if (!res.ok) return [];
  const data = await res.json();
  return (data?.items || [])
    .map((it: any): BookSuggestion => {
      const v = it.volumeInfo || {};
      const title = v.subtitle ? `${v.title}: ${v.subtitle}` : v.title || "";
      return {
        title,
        author: Array.isArray(v.authors) ? v.authors.slice(0, 2).join(", ") : "",
        coverUrl: googleThumb(v),
        year: (v.publishedDate || "").slice(0, 4),
        isbn: gIsbn(v),
        genre: classifyBest(v.categories),
      };
    })
    .filter((s: BookSuggestion) => s.title);
};

// Open Library search — fallback when Google returns nothing.
const searchOpenLibrary = async (q: string, signal?: AbortSignal): Promise<BookSuggestion[]> => {
  const params = new URLSearchParams({
    q,
    limit: "7",
    fields: "title,author_name,cover_i,cover_edition_key,isbn,first_publish_year,subject",
  });
  const res = await fetch(`https://openlibrary.org/search.json?${params.toString()}`, { signal });
  if (!res.ok) return [];
  const data = await res.json();
  return (data?.docs || [])
    .map((d: any): BookSuggestion => ({
      title: d.title || "",
      author: Array.isArray(d.author_name) ? d.author_name.slice(0, 2).join(", ") : "",
      coverUrl: d.cover_i
        ? `https://covers.openlibrary.org/b/id/${d.cover_i}-M.jpg`
        : d.cover_edition_key
        ? `https://covers.openlibrary.org/b/olid/${d.cover_edition_key}-M.jpg`
        : Array.isArray(d.isbn) && d.isbn[0]
        ? coverFromIsbn(d.isbn[0])
        : "",
      year: d.first_publish_year ? String(d.first_publish_year) : "",
      isbn: Array.isArray(d.isbn) && d.isbn[0] ? String(d.isbn[0]).replace(/[^0-9Xx]/g, "") : "",
      genre: classifyBest(d.subject),
    }))
    .filter((s: BookSuggestion) => s.title);
};

// Title autocomplete. Libris first (best Swedish catalogue), then Google Books
// (good international + covers), then Open Library as a last resort.
export const searchBooks = async (query: string, signal?: AbortSignal): Promise<BookSuggestion[]> => {
  const q = query.trim();
  if (q.length < 3) return [];
  for (const src of [searchLibris, searchGoogle, searchOpenLibrary]) {
    try {
      const hits = await src(q, signal);
      if (hits.length) return hits;
    } catch {
      /* try next source */
    }
  }
  return [];
};

// Strip Goodreads noise ("(Series, #2)") and subtitles for cleaner title search.
const cleanTitle = (t: string): string =>
  (t || "").replace(/\([^)]*\)/g, "").replace(/:.*$/, "").trim();

// iTunes/Apple Search — keyless, CORS-enabled, great cover coverage (incl. Swedish
// and audiobooks). Searching by title+author returns the correct book per title,
// so it avoids the "book 2 gets book 1's cover" problem. Returns a large artwork.
const itunesCover = async (title: string, author: string): Promise<string> => {
  const term = [cleanTitle(title), author].filter((s) => s && s.trim()).join(" ").trim();
  if (!term) return "";
  const lastName = (author || "").trim().split(/\s+/).pop()?.toLowerCase() || "";
  for (const media of ["ebook", "audiobook"]) {
    try {
      const res = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(term)}&media=${media}&limit=5`);
      if (!res.ok) continue;
      const results: any[] = (await res.json())?.results || [];
      if (!results.length) continue;
      const pick =
        (lastName && results.find((r) => String(r.artistName || "").toLowerCase().includes(lastName))) || results[0];
      const art = pick?.artworkUrl100 || pick?.artworkUrl60;
      if (art) return String(art).replace(/\/\d+x\d+bb\./, "/600x600bb.");
    } catch {
      /* try next media */
    }
  }
  return "";
};

// Look up cover + genre for a book. When an ISBN is known it pins the exact
// edition (so book 2 doesn't get book 1's cover) via Google → Open Library →
// the raw ISBN cover; a title search is only used when there's no ISBN.
export const fetchMeta = async (
  title: string,
  author: string,
  isbn = ""
): Promise<{ cover: string; genre: string }> => {
  // Cover: iTunes first (per-title, correct book, keyless). Fall back below.
  let cover = await itunesCover(title, author);
  let genre = "";

  if (isbn) {
    // Google by ISBN: exact-edition cover (if iTunes missed) + category.
    try {
      const res = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}&maxResults=1`);
      if (res.ok) {
        const v = (await res.json())?.items?.[0]?.volumeInfo;
        if (v) { if (!cover) cover = googleThumb(v); genre = classifyBest(v.categories); }
      }
    } catch {
      /* fall through */
    }
    if (!genre) {
      try {
        const hits = await searchOpenLibrary(isbn); // OL by ISBN → subjects (genre)
        if (hits[0]) genre = hits[0].genre;
      } catch {
        /* ignore */
      }
    }
    if (!cover) cover = coverFromIsbn(isbn); // exact ISBN cover (right or blank, never wrong)
    return { cover, genre };
  }

  const q = [cleanTitle(title), author].filter((s) => s && s.trim()).join(" ").trim();
  if (q) {
    for (const src of [searchGoogle, searchOpenLibrary]) {
      try {
        const hits = await src(q);
        const hit = hits.find((h) => h.coverUrl || h.genre);
        if (hit) { if (!cover) cover = hit.coverUrl; if (!genre) genre = hit.genre; }
      } catch {
        /* try next */
      }
      if (cover && genre) break;
    }
  }
  return { cover, genre };
};

// Cover-only helper for the "Hämta omslag" button.
export const fetchCover = async (title: string, author: string, isbn = ""): Promise<string> =>
  (await fetchMeta(title, author, isbn)).cover;

// ---- Goodreads CSV import ----

// Minimal CSV parser handling quoted fields, escaped quotes and newlines.
const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let inQ = false;
  for (let k = 0; k < text.length; k++) {
    const c = text[k];
    if (inQ) {
      if (c === '"') {
        if (text[k + 1] === '"') { cur += '"'; k++; }
        else inQ = false;
      } else cur += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n") { row.push(cur); rows.push(row); row = []; cur = ""; }
    else if (c !== "\r") cur += c;
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows;
};

const shelfToStatus = (shelf: string): BookStatus => {
  const s = shelf.trim().toLowerCase();
  if (s === "currently-reading") return "reading";
  if (s === "to-read") return "want";
  return "read";
};

// Parse a Goodreads library CSV export into books.
export const parseGoodreadsCsv = (text: string): Book[] => {
  const rows = parseCsv(text);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim());
  const idx = (name: string) => header.indexOf(name);
  const iTitle = idx("Title");
  const iAuthor = idx("Author");
  const iRating = idx("My Rating");
  const iShelf = idx("Exclusive Shelf");
  const iDateRead = idx("Date Read");
  const iDateAdded = idx("Date Added");
  const iIsbn13 = idx("ISBN13");
  const iIsbn = idx("ISBN");
  if (iTitle < 0) return [];
  const cleanIsbn = (s: string) => (s || "").replace(/[^0-9Xx]/g, "");
  const out: Book[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const title = (row[iTitle] || "").trim();
    if (!title) continue;
    const dateStr = (row[iDateRead] || row[iDateAdded] || "").trim();
    const ym = /(\d{4})/.exec(dateStr);
    const isbn = (iIsbn13 >= 0 ? cleanIsbn(row[iIsbn13]) : "") || (iIsbn >= 0 ? cleanIsbn(row[iIsbn]) : "");
    out.push({
      id: newId(),
      title,
      author: iAuthor >= 0 ? (row[iAuthor] || "").trim() : "",
      status: iShelf >= 0 ? shelfToStatus(row[iShelf] || "") : "read",
      rating: iRating >= 0 ? Math.max(0, Math.min(5, parseInt(row[iRating] || "0", 10) || 0)) : 0,
      source: "goodreads",
      format: "",
      year: ym ? ym[1] : "",
      notes: "",
      coverUrl: "", // filled by the background cover backfill (real images)
      isbn: isbn.length >= 10 ? isbn : "",
    });
  }
  return out;
};
